import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import { httpPost } from "../../services/commonService";
import DataCardGrid from "../../shared-components/DataCardGrid/DataCardGrid";
import { messageConstant } from "../../constants/messageConstant";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import SmartShipInvoice from "./smartShipInvoice";

const Membership = () => {
  const { t } = useTranslation();
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [reload, setReload] = useState(false);
  const [isCardDeletePopupOpen, setCardDeletePopupOpen] = useState(false);
  const [cardDeleteSelectedItem, setCardDeleteSelectedItem] = useState(null);
  const [paymentData, setPaymentData] = useState([]);
  const isPaymentProfileCreatedRef = useRef(false);
  const steps = [
    { id: 0, label: t("Payment Profile") },
    { id: 1, label: t("Memberships.PageTitle") },
  ];
  const [activeStep, setActiveStep] = useState(0);
  const handleTabChange = (id) => {
    setActiveStep(id);
  };
  const handleOpenDeletePopup = (row) => {
    setCardDeleteSelectedItem(row);
    setCardDeletePopupOpen(true);
  };

  const handleCloseDeletePopup = () => {
    setCardDeletePopupOpen(false);
  };

  useEffect(() => {
    const acceptURL = `${process.env.REACT_APP_ACCEPT_JS_URL}`;
    loadScript(acceptURL);
    window.responseHandlerHosted = responseHandlerHosted;
  }, []);

  const loadScript = (src) => {
    const tag = document.createElement("script");
    tag.src = src;
    document.body.appendChild(tag);
  };

  const columns = [
    {
      key: "UserPaymentProfileID",
      label: t("DualTeamBonusRpt.ID"),
      type: "text",
    },
    {
      key: "CardLastFourDigits",
      label: t("Memberships.Cards Last Four Digits"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "IsDefault",
      label: t("Memberships.Default Card"),
      type: "text",
    },
  ];

  const deletePaymentProfile = () => {
    const modelData = {
      Id: cardDeleteSelectedItem.UserPaymentProfileID,
    };
    const deleteURL = urlConstant.PaymentProfile.PaymentProfileDelete;
    httpPost(deleteURL, modelData, true, true, accessToken, impersonateEmail)
      .then((response) => {
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          showSnackbar(response.description, "success");
          setCardDeletePopupOpen(false);
          setReload(!reload);
        } else if (response.Status === dataConstant.APIResponseStatus.Failure) {
          showSnackbar(response.error, "error");
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const makeDefaultCard = (Data) => {
    if (Data?.UserPaymentProfileID !== dataConstant.Undefined) {
      const modelData = {
        Id: Data.UserPaymentProfileID,
      };
      const defaultSetURL = urlConstant.PaymentProfile.PaymentProfileSetDefault;
      httpPost(
        defaultSetURL,
        modelData,
        true,
        true,
        accessToken,
        impersonateEmail
      )
        .then((response) => {
          if (response.Status === dataConstant.APIResponseStatus.Success) {
            showSnackbar(response.description, "success");
            setReload(!reload);
          } else if (
            response.Status === dataConstant.APIResponseStatus.Failure
          ) {
            showSnackbar(response.error, "error");
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const gridButtons = [
    {
      id: "default",
      label: "Set As Default",
      icon: "default",
      color: "secondary-main",
      onClick: makeDefaultCard,
    },
    {
      id: "delete",
      label: "Delete",
      icon: "trash",
      color: "secondary-main",
      onClick: handleOpenDeletePopup,
    },
  ];

   const responseHandlerHosted = (data) => {
    if (isPaymentProfileCreatedRef.current) return;
    isPaymentProfileCreatedRef.current = true;
    if (data && data.messages.resultCode === "Ok") {
      const model = {
        PaymentDetails: [
          {
            Token: data.opaqueData.dataValue,
            CCLast4Digit: data.encryptedCardData.cardNumber,
            ExpireDate: data.encryptedCardData.expDate,
          },
        ],
      };
      const createURL = urlConstant.PaymentProfile.CreatePaymentProfile;
      httpPost(createURL, model, true, true, accessToken, impersonateEmail)
        .then((response) => {
          isPaymentProfileCreatedRef.current = false;
          showSnackbar(response.description, "success");
          setReload(!reload);
        })
        .catch((err) => {
          isPaymentProfileCreatedRef.current = false;
          console.error(err);
        });
    } else if (data && data.messages.resultCode === "Error") {
      let i = 0;
      let message = "";
      while (i < data.messages.message.length) {
        message = data.messages.message[i].text;
        i += 1;
      }
      if (message !== "") {
        showSnackbar(message, "error");
      }
    }
  };

  const fetchDataFunction = async ({
    page,
    pageSize,
    search,
    filters,
    sortKey,
    sortDirection,
  }) => {
    const pagingInfo = {
      PageNumber: page,
      RowsPerPage: pageSize,
      CommonSearchText: search,
      SortColumns: {
        SortColumnName: sortKey,
        SortOrder: sortDirection,
      },
      Filter: {
        CardLastFourDigitsFilter: filters?.CardLastFourDigits,
      },
    };
    const requestBody = {
      PagingInfoVM: pagingInfo,
    };
    try {
      const response = await httpPost(
        urlConstant.PaymentProfile.PaymentProfileList,
        requestBody,
        true,
        false,
        accessToken,
        impersonateEmail
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        const cardData = _.map(response.Value.Data.Data, (item) => ({
          ...item,
          isDelete: true,
          isDefault: item.IsDefault === "Yes" ? false : true,
        }));
        setPaymentData(cardData);
        return {
          data: cardData,
          totalCount: response.Value.Data.TotalCount,
        };
      } else {
        return { data: [], totalCount: 0 };
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Typography color="secondary.light" variant="h4" fontWeight={600}>
          {t("My Account")}
        </Typography>
        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              border: "1px solid var(--headerbg)",
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>
              <Typography color="secondary.light" variant="h4" fontWeight={600}>
                {t("Memberships.PageTitle")}
              </Typography>
              <Box sx={{ width: "100%", textAlign: "center" }}>
                <Stepper
                  sx={{ display: "inline-flex" }}
                  activeStep={activeStep}
                  alternativeLabel
                  connector={<></>}
                >
                  {steps.map((label) => (
                    <Step sx={{ mt: 0 }} key={label.id}>
                      <StepLabel
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          mt: 0,
                          "& .MuiStepLabel-label": {
                            mt: 0,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: "12px",
                            color:
                              activeStep === label.id ? "#F29B2A" : "#6B8EAC",
                          }}
                        >
                          <Button
                            sx={{
                              fontSize: "16px",
                              color: "var(--secondary-text)",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              textTransform: "none",
                            }}
                            onClick={() => handleTabChange(label.id)}
                          >
                            <Stack>
                              <Stack
                                sx={{ fontWeight: "400" }}
                                direction={"column"}
                              >
                                Step
                              </Stack>

                              <Stack direction={"column"}>{label.label}</Stack>
                            </Stack>
                          </Button>
                        </Box>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
              <Stack
                direction={"row"}
                gap={0}
                width={"100%"}
                alignItems={"center"}
                justifyContent={"center"}
                flexWrap={"wrap"}
              >
                <Button
                  variant="contained"
                  type="button"
                  className="AcceptUI"
                  data-billingaddressoptions='{"show":true, "required":true}'
                  data-apiloginid={process.env.REACT_APP_AUTHNET_API_LOGIN_KEY}
                  data-clientkey={process.env.REACT_APP_AUTHNET_API_CLIENT_KEY}
                  data-acceptuiformbtntxt={
                    messageConstant.Payment.SubmitFormTitle
                  }
                  data-acceptuiformheadertxt={
                    messageConstant.PaymentProfile.AddPaymentProfileButtonTitle
                  }
                  data-paymentoptions='{"showCreditCard": true, "showBankAccount": false}'
                  data-responsehandler="responseHandlerHosted"
                  //sx={{ display: 'none' }} // Hides the button initially
                  startIcon={
                    <LocalOfferOutlinedIcon
                      sx={{ width: "24px", height: "24px" }}
                    />
                  }
                >
                  {t("Add Payment Profile")}
                </Button>
              </Stack>
              {activeStep === 0 && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                    <DataCardGrid
                      key={reload}
                      onFetchData={fetchDataFunction}
                      columns={columns}
                      defaultItemsPerPage={8}
                      defaultColumnsPerGrid={3}
                      enableExport={true}
                      enablePagination={true}
                      uniqueId="paymentcard-grid"
                      actionButtons={gridButtons}
                    />
                  </Grid>
                </Grid>
              )}
              {activeStep === 1 && <SmartShipInvoice />}
            </Stack>
          </Card>
        </Box>
      </Box>

      <Dialog
        open={isCardDeletePopupOpen}
        onClose={handleCloseDeletePopup}
        className="login-popup"
        sx={{ borderRadius: "20px", background: "transparent" }}
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle>{t("Common.Delete")}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseDeletePopup}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              p: 1,
              borderRadius: "8px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={{
            background: "var(--background-card)!important",
            border: 0,
          }}
          dividers
        >
          <Stack direction={"column"} alignItems={"center"}>
            <Typography
              color="secondary.text"
              sx={{ fontSize: "16px", marginBottom: "20px" }}
              variant="body1"
            >
              {t("Are you sure you want to delete?")}
            </Typography>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <Button
              onClick={() => handleCloseDeletePopup()}
              variant="contained"
              color="secondary"
              sx={{ textTransform: "uppercase", width: "100%" }}
            >
              {t("Common.No")}
            </Button>
            <Button
              onClick={() => deletePaymentProfile()}
              variant="contained"
              color="primary"
              sx={{ textTransform: "uppercase", width: "100%" }}
            >
              {t("Common.Yes")}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};
export default Membership;
